import type { MarkdownFile, RuntimeConfig } from "./types";

interface GitTreeItem {
  path: string;
  type: string;
}

interface GitTreeResponse {
  tree: GitTreeItem[];
}

const memory = new Map<string, Promise<MarkdownFile[]>>();

export function clearContentCache(): void {
  memory.clear();
}

export async function fetchMarkdownFiles(config: RuntimeConfig): Promise<MarkdownFile[]> {
  const key = JSON.stringify(config.github);
  const cached = memory.get(key);
  if (cached) {
    return cached;
  }
  const promise = fetchMarkdownFilesUncached(config);
  memory.set(key, promise);
  return promise;
}

async function fetchMarkdownFilesUncached(config: RuntimeConfig): Promise<MarkdownFile[]> {
  const { owner, repo, ref, contentPath } = config.github;
  const apiBase = config.github.apiBase || "https://api.github.com";
  const rawBase = config.github.rawBase || "https://raw.githubusercontent.com";
  const treeURL = `${apiBase}/repos/${owner}/${repo}/git/trees/${encodeURIComponent(ref)}?recursive=1`;
  const response = await fetch(treeURL, { headers: { Accept: "application/vnd.github+json" } });
  if (!response.ok) {
    throw new Error(`GitHub tree request failed: ${response.status}`);
  }

  const payload = (await response.json()) as GitTreeResponse;
  const prefix = trimSlashes(contentPath);
  const files = payload.tree
    .filter((item) => item.type === "blob")
    .filter((item) => item.path.startsWith(`${prefix}/`) && item.path.endsWith(".md"))
    .sort((a, b) => a.path.localeCompare(b.path));

  return Promise.all(
    files.map(async (file) => {
      const rawURL = `${rawBase}/${owner}/${repo}/${encodeURIComponent(ref)}/${file.path
        .split("/")
        .map(encodeURIComponent)
        .join("/")}`;
      const raw = await fetch(rawURL);
      if (!raw.ok) {
        throw new Error(`Markdown request failed for ${file.path}: ${raw.status}`);
      }
      return {
        path: file.path,
        name: file.path.split("/").pop() || file.path,
        body: await raw.text(),
      };
    })
  );
}

function trimSlashes(value: string): string {
  return value.replace(/^\/+|\/+$/g, "");
}
